import { t, te } from "@/language/translation";
import { ErrorLink } from "@apollo/client/link/error";
import { fromPromise } from "@apollo/client/link/utils";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/vue";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { ApolloClient, ApolloLink } from "@apollo/client/core";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import cache from "./apolloCache";
import { transactionId } from "@/sentry";
import store from "@/store";
import {
  ensureFreshAccessToken,
  onAuthenticationTokenChanged,
  recoverFromSessionError,
} from "@/keycloak/AuthenticationSession";
import { isLISA3 } from "@/composables/useLISA3";
import { lisaToast } from "@fi-solutions/lisa-ui";

async function hashSHA256(input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

const httpLinkConfig = {
  uri: `${window.location.origin}/graphql`,
  headers: {
    "X-Version": import.meta.env.__VERSION__,
  },
};

const batchHttpLink = new BatchHttpLink(httpLinkConfig);

const nonBatchedHttpLink = createUploadLink({
  ...httpLinkConfig,
  headers: {
    ...httpLinkConfig.headers,
    "Apollo-Require-Preflight": "true",
  },
}) as unknown as ApolloLink;

const transactionIdMiddleware = new ApolloLink((operation, forward) => {
  const id = transactionId();
  // add adds a random Id to the header
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "X-Transaction-ID": id,
    },
  }));

  // generate unique transactionId and set as Sentry tag
  Sentry.getCurrentScope().setTag("transaction_id", id);

  return forward(operation);
}).concat(nonBatchedHttpLink);

const batchableQueries = [
  "SearchSite",
  "TCUserVacationStatistics",
  "GetChartData",
  "BDEGetProductionPlanInRange",
  "BDESystemStatus",
  "GetIHSeriesForIHParetoChart",
  "GSGBDECustomerRequirements",
  "BDEGetQAChecklistForProductionPlan",
  "BDEGetActiveShift",
  "GetAllMQTTRuleForGantt",
  "BDEGetChartLayoutForLiveOEE",
  "AuditQuestions",
  "EABShorthandsAndWorkflowForBoards",
  "IHConfig",
  "HLPConfigForSite",
  "SQDCWorkflowInstances",
  "SQDCWorkflowDefinitions",
  "SQDCEABShorthands",
  "GetUserForRevisionList",
  "SQDCActionDeadlineChanges",
];

const httpLink = ApolloLink.split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "query" &&
      batchableQueries.includes(definition.name?.value ?? "")
    );
  },
  batchHttpLink,
  transactionIdMiddleware,
);
let persistedHttpLink;

if (import.meta.env.PROD) {
  persistedHttpLink = createPersistedQueryLink({
    sha256: hashSHA256,
    disable({ graphQLErrors }) {
      if (import.meta.env.VITE_APP_DISABLE_PERSISTED_QUERIES || import.meta.env.MODE === "test") {
        return true;
      }
      // syntax after update to apollo client version >= 4
      // if (
      //   (CombinedGraphQLErrors.is(graphQLErrors) || CombinedProtocolErrors.is(graphQLErrors)) &&
      //   !graphQLErrors.errors.some((error) => error.message === "PersistedQueryNotSupported")
      // ) {
      if (
        graphQLErrors &&
        graphQLErrors.some((error) => {
          const { message } = error;
          return message === "PersistedQueryNotSupported";
        })
      ) {
        return true;
      }

      return false;
    },
  }).concat(httpLink);
}

const authedHttpLink = new ApolloLink((operation, forward) => {
  return fromPromise(ensureFreshAccessToken()).flatMap((token) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    }));
    return forward(operation);
  });
}).concat(persistedHttpLink ?? httpLink);

const sessionErrorMessages = new Set(["missingSession", "expiredSession", "invalidSession"]);

const errorLink = new ErrorLink(
  ({ operation, forward, networkError, graphQLErrors, protocolErrors }) => {
    const messages = [
      ...(graphQLErrors?.map(({ message }) => message) ?? []),
      ...(protocolErrors?.map(({ message }) => message) ?? []),
    ];
    const networkStatus =
      networkError != null && "statusCode" in networkError ? networkError.statusCode : undefined;
    const isSessionError =
      messages.some((message) => sessionErrorMessages.has(message)) || networkStatus === 401;

    if (isSessionError && operation.getContext().authenticationRetried !== true) {
      operation.setContext({ authenticationRetried: true });
      return fromPromise(recoverFromSessionError()).flatMap(() => forward(operation));
    }

    if (
      ["SQDCBoardGetName", "GetSiteIdForBoard", "EABShorthandsAndWorkflowForBoards"].includes(
        operation.operationName as string,
      )
    ) {
      return;
    }

    function handleError(message: string) {
      if (message === "VALID_LOGIN_NO_LISA_USER") {
        store.dispatch("login/setSetup", { state: true });
        return;
      }
      if (
        message === "missingSession" ||
        message === "expiredSession" ||
        message === "invalidSession"
      ) {
        window.$toast?.({
          severity: "error",
          summary: t("api.toast.error.title").toString(),
          detail: t("api.toast.error.expiredSession").toString(),
        });
        return;
      }

      let errorMessage: { message: string; severity?: "error" | "warning" };

      if (te(`api.toast.error.${message}`)) {
        errorMessage = {
          message: t(`api.toast.error.${message}`),
          severity: message === "UNAUTHORIZED" ? "warning" : "error",
        };
      } else {
        errorMessage = {
          message: t("api.toast.error.common"),
        };
      }
      if (isLISA3.value === true) {
        lisaToast({
          variant: errorMessage.severity ?? "error",
          duration: 5000,
          position: "bottom-right",
          title: t("api.toast.error.title").toString(),
          text: errorMessage?.message?.toString(),
        });
      } else {
        window.$toast?.({
          severity: errorMessage.severity ?? "error",
          life: 5000,
          summary: t("api.toast.error.title").toString(),
          detail: errorMessage?.message?.toString(),
        });
      }
    }

    if (graphQLErrors != null /**CombinedGraphQLErrors.is(error)*/) {
      const definition = getMainDefinition(operation.query);
      const transport =
        definition.kind === "OperationDefinition" && definition.operation === "subscription"
          ? "WebSocket"
          : "HTTP";
      graphQLErrors.forEach(({ message, locations, path }) => {
        // eslint-disable-next-line no-console
        console.error(
          `[GraphQL ${transport} error] ${operation.operationName || "anonymous"}: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
        handleError(message);
      });
    }
    if (protocolErrors != null /**CombinedProtocolErrors.is(error)*/) {
      protocolErrors.forEach(({ message, extensions }) => {
        // eslint-disable-next-line no-console
        console.error(
          `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
        );
        handleError(message);
      });
    }
    //else {
    if (networkError != null) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`);
      if (isLISA3.value === true) {
        lisaToast({
          variant: "error",
          position: "bottom-right",
          title: t("api.toast.error.title").toString(),
          text: t("api.toast.error.networkError").toString(),
        });
      } else {
        window.$toast?.({
          severity: "error",
          detail: t("api.toast.error.networkError").toString(),
          summary: t("api.toast.error.title").toString(),
        });
      }
    }
  },
);

const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";

// Create the subscription websocket link
export const wsLink = new GraphQLWsLink(
  createClient({
    url: `${wsProtocol}//${window.location.host}/graphql`,
    shouldRetry: () => true,
    retryAttempts: 5,
    on: {
      connected: () => window.dispatchEvent(new Event("lisa-graphql-ws-connected")),
    },
    connectionParams: async () => {
      const token = await ensureFreshAccessToken();
      return {
        authorization: `Bearer ${token}`,
      };
    },
  }),
);

onAuthenticationTokenChanged(() => wsLink.client.terminate());

// using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent
export function getApolloClientLink() {
  return ApolloLink.split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    authedHttpLink,
  );
}

export const apolloClient = new ApolloClient({
  clientAwareness: {
    name: "lisapp-frontend",
    version: import.meta.env.__VERSION__,
  },
  link: errorLink.concat(getApolloClientLink()),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  },
});

export default {
  apolloClient,
};
